import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadCaptureForm({ type = "buyer", title, subtitle }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        lead_type: type,
      });
      setSuccess(true);
      toast.success("You're in! Check your email for your guide.");
      setFormData({ name: "", email: "", phone: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="text-center py-8 animate-fadeIn"
        data-testid="lead-form-success"
      >
        <CheckCircle className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white mb-2">You're In!</h3>
        <p className="text-zinc-400">
          Check your email for your No-Bullshit{" "}
          {type === "buyer" ? "Buyer" : "Seller"} Guide.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`lead-capture-form-${type}`}>
      {title && (
        <div className="space-y-2">
          <h3 className="text-2xl md:text-3xl font-bold text-white">{title}</h3>
          {subtitle && <p className="text-zinc-400">{subtitle}</p>}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          type="text"
          placeholder="Your Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="bg-[#0A0A0A] border-white/20 rounded-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] placeholder:text-zinc-600 px-4 py-3 h-12"
          data-testid="lead-form-name-input"
        />
        <Input
          type="email"
          placeholder="Email Address"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          className="bg-[#0A0A0A] border-white/20 rounded-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] placeholder:text-zinc-600 px-4 py-3 h-12"
          data-testid="lead-form-email-input"
        />
        <Input
          type="tel"
          placeholder="Phone (Optional)"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="bg-[#0A0A0A] border-white/20 rounded-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] placeholder:text-zinc-600 px-4 py-3 h-12"
          data-testid="lead-form-phone-input"
        />
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] rounded-none font-bold uppercase tracking-widest h-12"
          data-testid="lead-form-submit-button"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            `Get My ${type === "buyer" ? "Buyer" : "Seller"} Guide`
          )}
        </Button>
      </form>
    </div>
  );
}
